import styled from "styled-components"

import { Select } from "@minuthq/meatball-ui-react"

import {
  DAY_MULTIPLIER,
  HOUR_MULTIPLIER,
  MINUTE_MULTIPLIER,
} from "src/data/guestCommunication/logic/guestCommunicationLogic"
import { useFetchGuestCommunicationTriggers } from "src/data/guestCommunication/queries/guestCommunicationQueries"
import { GuestCommunicationTrigger } from "src/data/guestCommunication/types/guestCommunicationTypes"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { spacing } from "src/ui/spacing"

const TIMING = [
  {
    langKey: langKeys.messaging_weeks_before,
    plurality: 2,
    name: "2 weeks before",
    seconds: DAY_MULTIPLIER * 14 * -1,
  },
  {
    langKey: langKeys.messaging_weeks_before,
    plurality: 1,
    name: "1 week before",
    seconds: DAY_MULTIPLIER * 7 * -1,
  },
  {
    langKey: langKeys.messaging_days_before,
    plurality: 3,
    name: "3 days before",
    seconds: DAY_MULTIPLIER * 3 * -1,
  },
  {
    langKey: langKeys.messaging_days_before,
    plurality: 2,
    name: "2 days before",
    seconds: DAY_MULTIPLIER * 2 * -1,
  },
  {
    langKey: langKeys.messaging_days_before,
    plurality: 1,
    name: "1 day before",
    seconds: DAY_MULTIPLIER * 1 * -1,
  },
  {
    langKey: langKeys.messaging_hours_before,
    plurality: 2,
    name: "2 hours before",
    seconds: HOUR_MULTIPLIER * 2 * -1,
  },
  {
    langKey: langKeys.messaging_hours_before,
    plurality: 1,
    name: "1 hour before",
    seconds: HOUR_MULTIPLIER * 1 * -1,
  },
  {
    langKey: langKeys.messaging_minutes_before,
    plurality: 30,
    name: "30 minutes before",
    seconds: MINUTE_MULTIPLIER * 30 * -1,
  },
  {
    langKey: langKeys.messaging_minutes_before,
    plurality: 15,
    name: "15 minutes before",
    seconds: MINUTE_MULTIPLIER * 15 * -1,
  },
  {
    langKey: langKeys.messaging_at_time_of,
    plurality: 0,
    name: "At time of",
    seconds: 0,
  },
  {
    langKey: langKeys.messaging_minutes_after,
    plurality: 15,
    name: "15 minutes after",
    seconds: MINUTE_MULTIPLIER * 15,
  },
  {
    langKey: langKeys.messaging_minutes_after,
    plurality: 30,
    name: "30 minutes after",
    seconds: MINUTE_MULTIPLIER * 30,
  },
  {
    langKey: langKeys.messaging_hours_after,
    plurality: 1,
    name: "1 hour after",
    seconds: HOUR_MULTIPLIER * 1,
  },
  {
    langKey: langKeys.messaging_hours_after,
    plurality: 2,
    name: "2 hours after",
    seconds: HOUR_MULTIPLIER * 2,
  },
  {
    langKey: langKeys.messaging_days_after,
    plurality: 1,
    name: "1 day after",
    seconds: DAY_MULTIPLIER * 1,
  },
  {
    langKey: langKeys.messaging_days_after,
    plurality: 2,
    name: "2 days after",
    seconds: DAY_MULTIPLIER * 2,
  },
  {
    langKey: langKeys.messaging_days_after,
    plurality: 3,
    name: "3 days after",
    seconds: DAY_MULTIPLIER * 3,
  },
  {
    langKey: langKeys.messaging_weeks_after,
    plurality: 1,
    name: "1 week after",
    seconds: DAY_MULTIPLIER * 7,
  },
  {
    langKey: langKeys.messaging_weeks_after,
    plurality: 2,
    name: "2 weeks after",
    seconds: DAY_MULTIPLIER * 14,
  },
]

export interface IScheduledForChangeEvent {
  triggerOffsetSeconds: number
  trigger: GuestCommunicationTrigger
}

export function ScheduledFor({
  triggerOffsetSeconds,
  trigger,
  onChange,
}: {
  triggerOffsetSeconds: number
  trigger: GuestCommunicationTrigger
  onChange: (schedule: IScheduledForChangeEvent) => void
}) {
  const fetchTriggers = useFetchGuestCommunicationTriggers({})
  const { t, langKeys } = useTranslate()

  const triggers = fetchTriggers.data

  function handleChange({
    trigger,
    triggerOffsetSeconds,
  }: IScheduledForChangeEvent) {
    onChange({ trigger, triggerOffsetSeconds })
  }

  if (!triggers) {
    return null // TODO WEB-502: Add loading state
  }

  return (
    <Box>
      <Select
        value={trigger}
        onChange={(value) => {
          const newTrigger = value as GuestCommunicationTrigger
          handleChange({
            triggerOffsetSeconds,
            trigger: newTrigger,
          })
        }}
        label={t(langKeys.messaging_trigger)}
        options={[
          { label: "", value: "", hidden: true },
          ...triggers.map((trigger) => ({
            label: trigger.trigger_name,
            value: trigger.trigger,
          })),
        ]}
      />
      <Select
        value={triggerOffsetSeconds.toString()}
        onChange={(value) => {
          const seconds = Number(value)
          handleChange({
            trigger,
            triggerOffsetSeconds: seconds,
          })
        }}
        label={t(langKeys.messaging_when_to_send)}
        options={[
          { label: "", value: "", hidden: true },
          ...TIMING.map((timing) => ({
            label: t(timing.langKey, { count: timing.plurality }),
            value: timing.seconds.toString(),
          })),
        ]}
      />
    </Box>
  )
}

const Box = styled.div`
  display: grid;
  grid-gap: ${spacing.L};
`
